import React, { useEffect, useState } from "react";
import { api, formatErr, API } from "../lib/api";
import { Link } from "react-router-dom";
import { Download, Trash2, FileText, FilePlus2, AlertTriangle, Package2, Mail, Check, Send, Archive } from "lucide-react";
import SendEmailDialog from "../components/SendEmailDialog";

export default function Dashboard() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [emailFor, setEmailFor] = useState(null);
  const [von, setVon] = useState("");
  const [bis, setBis] = useState("");
  const [zipBusy, setZipBusy] = useState(false);
  const [deliveryErr, setDeliveryErr] = useState(false);

  // Zustellstatus (Resend) abfragen; bei noch offenen Mails bis zu 3x erneut versuchen
  const refreshDelivery = async (attempt) => {
    try {
      const r = await api.post("/invoices/delivery-status");
      const st = r?.data?.statuses || {};
      setDeliveryErr(r?.data?.error === "forbidden");
      if (Object.keys(st).length) {
        setItems((prev) => prev.map((x) => (st[x.id] ? { ...x, delivery_status: st[x.id] } : x)));
      }
      const open = Object.values(st).some((v) => !["delivered","bounced","complained","failed","suppressed"].includes(v));
      if (open && attempt < 3) setTimeout(() => refreshDelivery(attempt + 1), 6000);
    } catch (e) { /* Status ist optional */ }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/invoices");
      setItems(data);
      refreshDelivery(0);
    }
    catch (e) { setErr(formatErr(e)); }
    finally { setLoading(false); }
  };

  const deliveryBadge = (s) => {
    switch (s) {
      case "delivered": return { t: "Zugestellt", c: "text-emerald-400" };
      case "bounced": return { t: "Nicht zustellbar (zurückgewiesen)", c: "text-red-400" };
      case "failed": return { t: "Versand fehlgeschlagen", c: "text-red-400" };
      case "suppressed": return { t: "Nicht zugestellt (Adresse gesperrt)", c: "text-red-400" };
      case "complained": return { t: "Als Spam gemeldet", c: "text-amber-400" };
      case "delivery_delayed": return { t: "Zustellung verzögert", c: "text-amber-400" };
      case "sent": case "queued": case "scheduled":
        return deliveryErr ? { t: "Status nicht abrufbar", c: "text-amber-400" } : { t: "Wird zugestellt …", c: "text-slate-400" };
      default: return null;
    }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Diese Rechnung aus der Historie entfernen?")) return;
    try { await api.delete(`/invoices/${id}`); setItems((p)=>p.filter(x=>x.id!==id)); }
    catch (e) { setErr(formatErr(e)); }
  };

  const downloadId = async (id, invoice_number, ext) => {
    try {
      const resp = await api.get(`/invoices/${id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const num = (invoice_number||"rechnung").replace(/[^A-Za-z0-9_-]/g,"_");
      const prefix = ext === "xml" ? "XRechnung" : "ZUGFeRD";
      const a = document.createElement("a"); a.href = url; a.download = `${prefix}_${num}.${ext}`; a.click();
      setTimeout(()=>URL.revokeObjectURL(url), 1500);
    } catch (e) { setErr(formatErr(e)); }
  };

  const downloadBackup = async () => {
    setErr(""); setZipBusy(true);
    try {
      const params = {};
      if (von) params.von = von;
      if (bis) params.bis = bis;
      const resp = await api.get("/invoices/export/zip", { params, responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Rechnungen_Backup_${new Date().toISOString().slice(0,10)}.zip`;
      a.click();
      setTimeout(()=>URL.revokeObjectURL(url), 1500);
    } catch (e) {
      if (e?.response?.status === 404) setErr("Keine Rechnungen im gewählten Zeitraum.");
      else setErr("Backup fehlgeschlagen. Bitte erneut versuchen.");
    } finally { setZipBusy(false); }
  };

  const stats = {
    total: items.length,
    zugferd: items.filter(x => x.format?.startsWith("zugferd")).length,
    xrechnung: items.filter(x => x.format === "xrechnung").length,
    gross: items.reduce((s,x)=>s+Number(x.gross_total||0),0),
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold">Historie</h1>
          <p className="text-slate-400 text-sm mt-1">Alle konvertierten E-Rechnungen auf einen Blick.</p>
        </div>
        <Link to="/app/convert" className="btn-amber" data-testid="new-conversion-cta"><FilePlus2 size={16}/>Neue Rechnung konvertieren</Link>
      </div>

      {deliveryErr && <div className="card border-amber-500/40 bg-amber-500/5 p-4 text-sm text-amber-200" data-testid="delivery-key-hint">
        Der Zustellstatus kann nicht abgerufen werden: Der Resend-API-Schlüssel hat nur Sende-Rechte. Bitte in Resend einen Schlüssel mit „Full access“ anlegen und als RESEND_API_KEY hinterlegen.
      </div>}
      {err && <div className="card border-rose-500/40 bg-rose-500/5 p-4 flex items-start gap-3">
        <AlertTriangle size={18} className="text-rose-400 shrink-0 mt-0.5"/><div className="text-rose-200 text-sm">{err}</div>
      </div>}

      <div className="grid sm:grid-cols-4 gap-4">
        <Stat label="Konvertierungen" value={stats.total} testid="stat-total"/>
        <Stat label="ZUGFeRD / Factur-X" value={stats.zugferd} accent="text-blue-300" testid="stat-zugferd"/>
        <Stat label="XRechnung" value={stats.xrechnung} accent="text-amber-300" testid="stat-xrechnung"/>
        <Stat label="Summe Brutto" value={stats.gross.toLocaleString("de-DE",{minimumFractionDigits:2,maximumFractionDigits:2}) + " €"} accent="text-emerald-300" testid="stat-gross"/>
      </div>

      <div className="card p-4 flex flex-col md:flex-row md:items-end gap-3" data-testid="backup-box">
        <div className="flex-1">
          <div className="font-medium flex items-center gap-2"><Archive size={16}/>Backup aller Rechnungen</div>
          <div className="text-xs text-slate-400 mt-1">ZIP mit allen PDFs/XMLs und einer Übersicht (CSV). Zeitraum optional, bezogen auf das Konvertierungsdatum.</div>
        </div>
        <label className="text-xs text-slate-400">Von
          <input type="date" value={von} onChange={(e)=>setVon(e.target.value)} className="block mt-1 rounded-lg bg-slate-900 border border-slate-700 px-3 py-2 text-sm text-slate-100" data-testid="backup-von"/>
        </label>
        <label className="text-xs text-slate-400">Bis
          <input type="date" value={bis} onChange={(e)=>setBis(e.target.value)} className="block mt-1 rounded-lg bg-slate-900 border border-slate-700 px-3 py-2 text-sm text-slate-100" data-testid="backup-bis"/>
        </label>
        <button className="btn-primary" onClick={downloadBackup} disabled={zipBusy || items.length === 0} data-testid="backup-btn">
          <Archive size={16}/>{zipBusy ? "Erstelle ZIP..." : "Backup herunterladen"}
        </button>
      </div>

      <div className="card overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-slate-400" data-testid="history-loading">Lade Historie...</div>
        ) : items.length === 0 ? (
          <div className="p-14 text-center" data-testid="history-empty">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-slate-800/70 border border-slate-700 flex items-center justify-center mb-4">
              <Package2 size={28} className="text-slate-500"/>
            </div>
            <div className="font-display text-xl font-semibold mb-1">Noch keine Rechnungen</div>
            <div className="text-slate-400 text-sm mb-5">Konvertieren Sie Ihre erste freewarefaktura PDF zu ZUGFeRD oder XRechnung.</div>
            <Link to="/app/convert" className="btn-primary"><FilePlus2 size={16}/>Jetzt starten</Link>
          </div>
        ) : (
          <table className="tbl" data-testid="history-table">
            <thead>
              <tr>
                <th>Rechnung</th>
                <th>Datum</th>
                <th>Format</th>
                <th>Brutto</th>
                <th>Erstellt</th>
                <th>Zuletzt gesendet</th>
                <th className="text-right">Aktion</th>
              </tr>
            </thead>
            <tbody>
              {items.map((x, i)=>(
                <tr key={x.id} data-testid={`history-row-${i}`}>
                  <td>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-blue-300">
                        <FileText size={16}/>
                      </div>
                      <div>
                        <div className="font-medium">{x.invoice_number || "—"}</div>
                        <div className="text-xs font-mono text-slate-500">{x.id.slice(0,8)}</div>
                      </div>
                    </div>
                  </td>
                  <td className="font-mono text-sm text-slate-300">{x.issue_date || "—"}</td>
                  <td>
                    {x.format === "xrechnung" ? (
                      <span className="chip chip-pending">XRechnung</span>
                    ) : x.format === "zugferd_basic" ? (
                      <span className="chip chip-draft">ZUGFeRD BASIC</span>
                    ) : (
                      <span className="chip chip-valid">ZUGFeRD EN16931</span>
                    )}
                  </td>
                  <td className="font-mono">{Number(x.gross_total||0).toLocaleString("de-DE",{minimumFractionDigits:2,maximumFractionDigits:2})} {x.currency}</td>
                  <td className="text-xs font-mono text-slate-400">{new Date(x.created_at).toLocaleString("de-DE")}</td>
                  <td data-testid={`history-sent-${i}`}>
                    {x.last_sent_to ? (
                      <div className="flex flex-col gap-0.5 min-w-0">
                        <div className="text-xs text-slate-200 truncate max-w-[180px]" title={x.last_sent_to}>
                          <Check size={11} className="inline text-emerald-400 mr-1"/>{x.last_sent_to}
                        </div>
                        <div className="text-[10px] font-mono text-slate-500">{new Date(x.last_sent_at).toLocaleString("de-DE")}</div>
                        {deliveryBadge(x.delivery_status) && (
                          <div className={`text-[11px] font-medium ${deliveryBadge(x.delivery_status).c}`} data-testid={`history-delivery-${i}`}>
                            {deliveryBadge(x.delivery_status).t}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-slate-500">—</span>
                    )}
                  </td>
                  <td className="text-right">
                    <div className="inline-flex gap-2">
                      <button className="btn-ghost" onClick={()=>downloadId(x.id, x.invoice_number, x.ext)} data-testid={`download-btn-${i}`}><Download size={14}/>Download</button>
                      <button className="btn-ghost" onClick={()=>setEmailFor(x)} data-testid={`email-btn-${i}`} title={x.last_sent_to ? `Erneut senden an ${x.last_sent_to}` : "Per E-Mail versenden"}>
                        {x.last_sent_to ? <><Send size={14} className="text-emerald-400"/>Erneut senden</> : <><Mail size={14}/>E-Mail</>}
                      </button>
                      <button className="btn-danger" onClick={()=>remove(x.id)} data-testid={`delete-btn-${i}`}><Trash2 size={14}/></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {emailFor && (
        <SendEmailDialog
          invoiceId={emailFor.id}
          defaults={{ to_email: emailFor.last_sent_to || "" }}
          onClose={()=>{ setEmailFor(null); load(); }}
        />
      )}
    </div>
  );
}

const Stat = ({label, value, accent="text-slate-100", testid}) => (
  <div className="card p-4" data-testid={testid}>
    <div className="text-xs font-mono uppercase tracking-wider text-slate-500">{label}</div>
    <div className={`font-display text-2xl font-bold mt-1 ${accent}`}>{value}</div>
  </div>
);
